import React, { useMemo } from "react";

/**
 * Render a Calendly inline embed without ever writing user-provided HTML to
 * the DOM. The consultant pastes Calendly's "embed inline" snippet into their
 * settings; that snippet looks like:
 *
 *   <div class="calendly-inline-widget" data-url="https://calendly.com/..." />
 *   <script src="https://assets.calendly.com/assets/external/widget.js" async />
 *
 * We extract:
 *   1. the URL from `data-url="..."` or `src="..."`
 *   2. any explicit width/height styles
 * and render a plain <iframe sandbox="allow-scripts allow-same-origin ..."> to
 * the URL. No innerHTML, no dangerouslySetInnerHTML, no arbitrary <script>
 * execution — the embed runs inside the sandboxed cross-origin iframe just
 * like the official Calendly widget renders it.
 */

const URL_REGEX = /(?:data-url|src)\s*=\s*["']((?:https?:)?\/\/(?:[a-z0-9-]+\.)*calendly\.com\/[^"']+)["']/i;
const STYLE_HEIGHT_REGEX = /height\s*:\s*([0-9.]+(?:px|%|em|rem|vh))/i;
const STYLE_WIDTH_REGEX = /(?:min-)?width\s*:\s*([0-9.]+(?:px|%|em|rem|vw))/i;

function extractEmbedUrl(html) {
  if (!html) return null;
  const m = html.match(URL_REGEX);
  if (!m) return null;
  let url = m[1];
  if (url.startsWith("//")) url = "https:" + url;
  // Only accept https calendly.com URLs — guards against open redirects.
  try {
    const u = new URL(url);
    if (u.protocol !== "https:") return null;
    if (!u.hostname.endsWith("calendly.com")) return null;
    return u.toString();
  } catch {
    return null;
  }
}

function extractSize(html) {
  const h = html?.match(STYLE_HEIGHT_REGEX)?.[1];
  const w = html?.match(STYLE_WIDTH_REGEX)?.[1];
  return { width: w || "100%", height: h || "700px" };
}

export default function CalendlyEmbed({ html, className }) {
  const { url, width, height } = useMemo(() => {
    const url = extractEmbedUrl(html);
    const size = extractSize(html);
    return { url, width: size.width, height: size.height };
  }, [html]);

  if (!url) {
    return (
      <div className={className} data-testid="calendly-embed-empty">
        <p className="text-sm text-zinc-500">
          Plak je Calendly embed-code in je instellingen om hier je agenda te tonen.
        </p>
      </div>
    );
  }

  return (
    <div className={className} data-testid="calendly-embed">
      <iframe
        title="Calendly"
        src={url}
        style={{ width, height, border: 0 }}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
        loading="lazy"
        data-testid="calendly-iframe"
      />
    </div>
  );
}
